/**
 * Shared CRM building blocks. Pages under pages/crm import from here so a
 * stage, a status, a rupee amount and a file link look and behave the same
 * on every screen.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CRM_STAGE, money, type CrmStage, type StatusMeta } from '@adisys/shared';
import type { LucideIcon } from 'lucide-react';
import { api, ApiRequestError, fileObjectUrl } from './api';
import { Skeleton, StatusBadge, cx, useToast } from '../components/ui';

/** Every project tab receives the loaded project and a way to refresh it after a change. */
export interface TabProps {
  project: any;
  /** Re-fetches the project header and summary (stage may have moved). */
  reload: () => void;
}

export const projectKey = (id: string) => ['crm', 'project', id] as const;

/** Invalidate everything that depends on one project after a write. */
export function useReloadProject(projectId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['crm', 'project', projectId] });
    void qc.invalidateQueries({ queryKey: ['crm', 'projects'] });
    void qc.invalidateQueries({ queryKey: ['crm', 'dashboard'] });
  };
}

/** Lookups for forms: solutions, industries, sources and the people who can be assigned. */
export function useCrmMeta() {
  return useQuery({ queryKey: ['crm', 'meta'], queryFn: () => api.get('/crm/meta'), staleTime: 300_000 });
}

/** A message for the user from whatever a mutation threw. */
export function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) {
    const first = err.details ? Object.values(err.details)[0]?.[0] : undefined;
    return first && first !== err.message ? `${err.message} ${first}` : err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

/** Opens an attachment through the authorised file route (files are never public URLs). */
export function useOpenFile() {
  const toast = useToast();
  return async (fileId: string) => {
    try {
      const url = await fileObjectUrl(fileId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error('Could not open the file', errorText(e));
    }
  };
}

export const inr = (n: number | string | null | undefined, compact = false) => money(n, { compact });
/** Two-decimal rupee amount, for invoices and totals where the paise matter. */
export const inr2 = (n: number | string | null | undefined) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n ?? 0));

export const qtyLabel = (n: number | string) => {
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '');
};

// ---------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------
export function StageBadge({ stage }: { stage: CrmStage }) {
  const m = CRM_STAGE.byValue[stage];
  return <StatusBadge tone={m.tone} title={m.description}>{m.label}</StatusBadge>;
}

/** Renders any `meta()` vocabulary from @adisys/shared as a badge. */
export function MetaBadge<T extends string>({ meta, value }: {
  meta: { byValue: Record<T, StatusMeta<T>> }; value: T;
}) {
  const m = meta.byValue[value];
  if (!m) return <StatusBadge>{String(value)}</StatusBadge>;
  return <StatusBadge tone={m.tone} title={m.description}>{m.label}</StatusBadge>;
}

// ---------------------------------------------------------------------
// Small layout helpers
// ---------------------------------------------------------------------
export function Kpi({ label, value, sub, icon: Icon, tone = 'neutral', to }: {
  label: string; value: string; sub?: string; icon: LucideIcon;
  tone?: 'neutral' | 'warning' | 'danger' | 'success'; to?: string;
}) {
  const accent = { neutral: 'text-ink-400', warning: 'text-warning', danger: 'text-danger', success: 'text-success' }[tone];
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-ink-500">{label}</p>
        <Icon className={cx('h-4 w-4 shrink-0', accent)} aria-hidden />
      </div>
      <p className="tabular mt-2 text-2xl font-semibold tracking-tight text-ink-900">{value}</p>
      {sub && <p className={cx('mt-0.5 text-xs', tone === 'neutral' ? 'text-ink-500' : accent)}>{sub}</p>}
    </>
  );
  const cls = 'rounded-card bg-card p-4 shadow-card ring-1 ring-line';
  return to
    ? <Link to={to} className={cx(cls, 'transition-shadow hover:shadow-raised')}>{body}</Link>
    : <div className={cls}>{body}</div>;
}

export function KpiSkeleton() {
  return (
    <div className="rounded-card bg-card p-4 shadow-card ring-1 ring-line">
      <Skeleton className="h-3 w-24" /><Skeleton className="mt-3 h-7 w-16" /><Skeleton className="mt-2 h-3 w-20" />
    </div>
  );
}

/** A labelled value in a definition grid. */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-ink-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-ink-900">{children || <span className="text-ink-400">—</span>}</dd>
    </div>
  );
}

/** Titled block used inside tabs. */
export function Section({ title, description, action, children }: {
  title: string; description?: string; action?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-ink-500">{description}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/** Explains why an action is unavailable, instead of silently hiding it. */
export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warning' | 'danger' | 'success'; children: ReactNode }) {
  const cls = {
    info: 'bg-info-soft text-info', warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger', success: 'bg-success-soft text-success',
  }[tone];
  return <div className={cx('rounded-lg px-3.5 py-2.5 text-sm leading-relaxed', cls)}>{children}</div>;
}
